function debounce(fn, delay = 200) {
  let timer = null;
  return function (...args) {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}
var ValentineTimeline = class extends HTMLElement {
  constructor() {
    super();
    this.timelineItems = this.querySelectorAll(".valentine-timeline-item");
    this.updateInterval = null;

    // 解析时区偏移字符串（支持 "UTC-8", "UTC+8", "-8", "+8" 等格式）
    const timezoneStr = this.getAttribute("data-timezone-offset") || "UTC-8";
    this.timezoneOffset = this.parseTimezoneOffset(timezoneStr);

    this.init();
  }

  /**
   * 解析时区偏移字符串
   * 支持格式: "UTC-8", "UTC+8", "-8", "+8", "8", "-8.5", "UTC+5.5"
   * @param {string} str - 时区偏移字符串
   * @returns {number} - 数值偏移量
   */
  parseTimezoneOffset(str) {
    if (!str) return -8; // 默认 UTC-8

    // 移除 "UTC" 前缀和空格
    const cleaned = str.replace(/UTC/i, "").trim();

    // 解析数字（支持正负号和小数）
    const offset = parseFloat(cleaned);

    // 验证范围 (-12 到 +14)
    if (isNaN(offset) || offset < -12 || offset > 14) {
      return -8;
    }

    return offset;
  }

  init() {
    // 页面加载时检查时间并更新状态
    this.checkTimeAndUpdate();

    // 每分钟检查一次时间状态，自动更新
    this.updateInterval = setInterval(() => {
      this.checkTimeAndUpdate();
    }, 60000); // 每60秒检查一次
    const resizeHandler = debounce(() => {
      this.updateTimelineProgress();
    }, 300);
    window.addEventListener("resize", resizeHandler);
  }

  /**
   * 将时间字符串转换为指定 UTC 偏移的 Date 对象
   * @param {string} timeString - 时间字符串
   *        支持格式:
   *        - YYYY-MM-DD HH:MM:SS (例如: 2026-02-01 00:00:00)
   *        - YYYY-MM-DDTHH:MM:SS (例如: 2026-02-01T00:00:00)
   * @param {number} utcOffset - UTC 偏移量（小时），例如 +8 表示 UTC+8
   * @returns {Date} - Date 对象
   */
  parseTimeWithOffset(timeString, utcOffset) {
    if (!timeString) return null;

    try {
      // 如果时间字符串已包含时区信息，直接解析
      if (timeString.includes("Z") || timeString.match(/[+-]\d{2}:\d{2}$/)) {
        return new Date(timeString);
      }

      // 解析时间组件（支持 T 或空格分隔）
      // 匹配: YYYY-MM-DD HH:MM:SS 或 YYYY-MM-DDTHH:MM:SS
      const match = timeString.match(/(\d{4})-(\d{2})-(\d{2})[\sT](\d{2}):(\d{2}):(\d{2})/);
      if (!match) {
        return null;
      }

      const [, year, month, day, hour, minute, second] = match;

      // 将时间视为指定 UTC 偏移的本地时间
      // 例如: "2026-02-01 00:00:00" 在 UTC+8
      // 需要转换为 UTC 时间: 2026-01-31 16:00:00 UTC

      // 创建该时区的本地时间（作为 UTC 时间）
      const localTime = Date.UTC(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        parseInt(hour),
        parseInt(minute),
        parseInt(second),
      );

      // 减去时区偏移，得到 UTC 时间
      // 如果是 UTC+8，则需要减去 8 小时
      // 如果是 UTC-8，则需要加上 8 小时（减去 -8）
      const utcTime = localTime - utcOffset * 60 * 60 * 1000;

      return new Date(utcTime);
    } catch (error) {
      return null;
    }
  }

  /**
   * 从 data 属性获取时间范围（考虑时区偏移）
   */
  getTimeRange(item) {
    const startTime = item.getAttribute("data-start-time");
    const endTime = item.getAttribute("data-end-time");

    if (!startTime || !endTime) return null;

    try {
      // 使用 UTC 偏移解析时间
      const start = this.parseTimeWithOffset(startTime, this.timezoneOffset);
      const end = this.parseTimeWithOffset(endTime, this.timezoneOffset);

      // 验证日期是否有效
      if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
        return null;
      }

      return { start, end };
    } catch (error) {
      return null;
    }
  }

  /**
   * 检查当前时间是否在指定的日期范围内
   */
  isCurrentTimeInRange(timeRange) {
    if (!timeRange) return false;

    const now = new Date();
    return now >= timeRange.start && now <= timeRange.end;
  }

  /**
   * 检查当前时间状态
   * @returns {Object} { currentIndex: 当前激活的索引, nextIndex: 下一个即将激活的索引 }
   */
  getCurrentTimeStatus() {
    const now = new Date();
    let currentIndex = 0;
    let nextIndex = 0;
    let closestFuture = null;

    this.timelineItems.forEach((item, index) => {
      const timeRange = this.getTimeRange(item);
      if (!timeRange) return;

      // 检查是否在当前时间范围内
      if (this.isCurrentTimeInRange(timeRange)) {
        currentIndex = index;
      }

      // 找到最近的未来时间段
      if (timeRange.start > now) {
        if (!closestFuture || timeRange.start < closestFuture) {
          closestFuture = timeRange.start;
          nextIndex = index;
        }
      }
    });

    return { currentIndex, nextIndex };
  }

  /**
   * 检查时间并更新状态
   */
  checkTimeAndUpdate() {
    const { currentIndex, nextIndex } = this.getCurrentTimeStatus();
    this.currentIndex = currentIndex;
    // 先移除所有激活状态
    this.timelineItems.forEach((item) => {
      item.classList.remove("active", "is-current", "active-period", "upcoming");
    });
    // 更新时间线的进度条

    setTimeout(() => {
      this.updateTimelineProgress(this.currentIndex);
    }, 100);

    // 如果有当前激活的时间段
    if (currentIndex >= 0) {
      const activeItem = this.timelineItems[currentIndex];
      activeItem.classList.add("active");

      // 已完成的项目也标记为 active
      this.timelineItems.forEach((item, index) => {
        if (index < currentIndex) {
          item.classList.add("is-current");
        }
      });

      // 打印当前激活的时间段信息
      this.logCurrentStatus(currentIndex);
    }
    // 如果没有当前激活的，但有即将到来的
    else if (nextIndex >= 0) {
      this.timelineItems[nextIndex].classList.add("active");
    }
    // 如果所有时间段都已过期，激活最后一个
    else if (nextIndex === -1 && currentIndex === -1) {
      const lastIndex = this.timelineItems.length - 1;
      if (lastIndex >= 0) {
        this.timelineItems.forEach((item) => {
          item.classList.add("active", "completed");
        });
      }
    }
  }

  /**
   * 更新时间线进度条
   */
  updateTimelineProgress() {
    const currentIndex = this.currentIndex;
    const timelineLine = this.querySelector(".valentine-timeline-line");
    // 更新 CSS 变量来控制进度条
    const items = this.querySelector(".valentine-timeline-items");
    const itemsRect = items.getBoundingClientRect();
    const itemIndex = this.timelineItems[currentIndex];
    const itemIndexRect = itemIndex.getBoundingClientRect();
    const itemIndexConter = itemIndex.querySelector(".timeline-item-content").getBoundingClientRect();
    let lineWidth = itemIndexConter.x - itemsRect.x + itemIndexConter.width / 2;
    let lineHeight = itemIndexConter.y - itemIndexRect.y + itemIndexConter.height / 2;
    if (items) {
      items.style.setProperty("--line-height", lineHeight + "px");
      items.style.setProperty("--line-width", lineWidth + "px");
      if (!timelineLine || currentIndex < 0) return;
      items.style.setProperty("--line-active-index", currentIndex);
    }
  }

  /**
   * 格式化时间到指定 UTC 偏移
   */
  formatTimeInOffset(date, offset) {
    const utcTime = date.getTime() + date.getTimezoneOffset() * 60000;
    const offsetTime = new Date(utcTime + offset * 3600000);
    return offsetTime.toLocaleString("zh-CN", { timeZone: "UTC" }).replace(/\//g, "-");
  }

  /**
   * 打印当前状态信息（用于调试）
   */
  logCurrentStatus(currentIndex) {
    const currentItem = this.timelineItems[currentIndex];
    const timeRange = this.getTimeRange(currentItem);
    const periodTitle = currentItem.querySelector(".timeline-period")?.textContent || "";
    const startTimeRaw = currentItem.getAttribute("data-start-time");
    const endTimeRaw = currentItem.getAttribute("data-end-time");
  }

  /**
   * 组件断开连接时清理定时器
   */
  disconnectedCallback() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }
  }
};

if (!customElements.get("valentine-timeline")) {
  customElements.define("valentine-timeline", ValentineTimeline);
}
